// OFF-C-02 (ADR-128): the Seller's sales and collections made without signal wait here, in order, with the command id
// they will be sent under; the server decides on each. A refusal stops the queue until the Seller edits or deletes the
// item; a lost connection just leaves the rest waiting. The queue works on any OutboxStore (IndexedDB on the phone).

export type OutboxStatus = 'waiting' | 'sending' | 'refused';
export interface OutboxItem {
  /** The command id: a replay after a lost reply returns the first result, never a second posting. */
  id: string;
  /** Whose it is: a phone handed to another user never shows or sends the first user's items. */
  user: string;
  seq: number;
  /** "Pending-n" on screen until the server gives the real number. */
  pending: number;
  operation: string;
  params: Record<string, unknown>;
  kind: 'sale' | 'collection';
  customer: string;
  total: number;
  createdAt: string;
  status: OutboxStatus;
  refusal?: { code: string; detail: Record<string, unknown> };
}
export interface OutboxStore {
  all(): Promise<OutboxItem[]>;
  put(item: OutboxItem): Promise<void>;
  remove(id: string): Promise<void>;
}
export class MemoryStore implements OutboxStore {
  private items = new Map<string, OutboxItem>();
  async all() {
    return [...this.items.values()].sort((a, b) => a.seq - b.seq);
  }
  async put(item: OutboxItem) {
    this.items.set(item.id, structuredClone(item));
  }
  async remove(id: string) {
    this.items.delete(id);
  }
}

/** How a send ended: ok, still offline (keep waiting), or the server's refusal (stop and ask). */
export type SendResult =
  | { ok: true }
  | { ok: false; offline: true }
  | { ok: false; offline: false; code: string; detail: Record<string, unknown> };
export type Sender = (item: OutboxItem) => Promise<SendResult>;

export class Outbox {
  private items: OutboxItem[] = [];
  private listeners = new Set<() => void>();
  private flushing = false;
  constructor(private store: OutboxStore) {}

  private user = '';
  async load(): Promise<void> {
    this.items = (await this.store.all()).filter((i) => i.user === this.user);
    this.changed();
  }
  /** The signed-in user: only their items are listed, sent or numbered; the others stay stored for their owner. */
  async setUser(user: string): Promise<void> {
    this.user = user;
    await this.load();
  }
  list(): readonly OutboxItem[] {
    return this.items;
  }
  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  private changed() {
    for (const fn of this.listeners) fn();
  }

  async enqueue(
    input: Pick<OutboxItem, 'id' | 'operation' | 'params' | 'kind' | 'customer' | 'total'>,
  ): Promise<OutboxItem> {
    const last = this.items[this.items.length - 1];
    const item: OutboxItem = {
      ...input,
      user: this.user,
      seq: (last?.seq ?? 0) + 1,
      pending: this.items.length === 0 ? 1 : Math.max(...this.items.map((i) => i.pending)) + 1,
      createdAt: new Date().toISOString(),
      status: 'waiting',
    };
    await this.store.put(item);
    this.items = [...this.items, item];
    this.changed();
    return item;
  }

  /** The item that stops the queue, if any (the first refused one). */
  held(): OutboxItem | undefined {
    return this.items.find((i) => i.status === 'refused');
  }

  /** Sends the waiting items in order. Stops at a refusal or when the signal is gone. Returns how many were sent. */
  async flush(send: Sender): Promise<number> {
    if (this.flushing || this.held()) return 0;
    this.flushing = true;
    let sent = 0;
    try {
      for (const item of [...this.items]) {
        await this.set(item, { status: 'sending' });
        const r = await send(item);
        if (r.ok) {
          await this.store.remove(item.id);
          this.items = this.items.filter((i) => i.id !== item.id);
          sent += 1;
          this.changed();
          continue;
        }
        if (r.offline) {
          await this.set(item, { status: 'waiting' });
          break;
        }
        await this.set(item, { status: 'refused', refusal: { code: r.code, detail: r.detail } });
        break;
      }
    } finally {
      this.flushing = false;
    }
    return sent;
  }

  private async set(item: OutboxItem, patch: Partial<OutboxItem>) {
    const next = { ...item, ...patch };
    await this.store.put(next);
    this.items = this.items.map((i) => (i.id === item.id ? next : i));
    this.changed();
  }

  /** Deleting an item (waiting or refused) is the Seller's choice; the rest of the queue then continues. */
  async remove(id: string): Promise<void> {
    await this.store.remove(id);
    this.items = this.items.filter((i) => i.id !== id);
    this.changed();
  }
  /** A refused item taken back for editing: it leaves the queue, the corrected sale is queued again as a new item. */
  async takeForEdit(id: string): Promise<OutboxItem | undefined> {
    const item = this.items.find((i) => i.id === id);
    if (item) await this.remove(id);
    return item;
  }

  /** True when something has waited more than a day (ADR-128: ask the Seller to connect). */
  overdue(now: Date = new Date()): boolean {
    const first = this.items[0];
    return first !== undefined && now.getTime() - new Date(first.createdAt).getTime() > 24 * 3600 * 1000;
  }
}
